import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Header from '../components/Header';
import Footer from '../components/Footer';
import LazyImage, { LazyBackgroundImage } from '../components/LazyImage';
import { 
  Building2, Users, ArrowRight, CheckCircle, 
  ChevronDown, Shield, Target, FileCheck, Handshake
} from 'lucide-react';

const HomePage = () => {
  const navigate = useNavigate();
  const [showRegisterDropdown, setShowRegisterDropdown] = useState(false);
  const [showPartnersDropdown, setShowPartnersDropdown] = useState(false);
  const [cardsVisible, setCardsVisible] = useState(false);
  const registerDropdownRef = useRef(null);
  const partnersDropdownRef = useRef(null);
  const whyTmgRef = useRef(null);

  // Warm Premium Palette - NO GREY
  const colors = {
    primaryTeal: '#3D6B67',
    primaryTealLight: '#4A7D78',
    primaryBrown: '#5C4033',
    primaryBrownDark: '#4A3328',
    bgCream: '#FAF6F2',
    bgSage: '#DCE7E3',
    bgCard: '#FFFFFF',
    bgWarmWhite: '#FFFDFB',
    textPrimary: '#2C1810',
    textSecondary: '#5D4E47',
    textTertiary: '#8B7D76',
    textOnDark: '#FFFFFF',
    textOnDarkMuted: '#E8E4E1',
    borderWarm: '#E8E0D8',
    borderSage: '#C5D5CF',
    accentGold: '#C9A96E',
    success: '#4D9078',
  };

  const whyFeatures = [
    { icon: Shield, title: 'Compliance-First', description: 'Schedule VII aligned, audit-ready documentation for seamless regulatory adherence.' },
    { icon: Target, title: 'Verified Network', description: '80G, 12A, FCRA verified partners ensuring credibility and trust.' },
    { icon: FileCheck, title: 'End-to-End', description: 'From discovery to impact reporting — complete lifecycle management.' },
    { icon: Handshake, title: 'Hands-On Support', description: 'Dedicated onboarding assistance and continuous partnership.' }
  ];

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (registerDropdownRef.current && !registerDropdownRef.current.contains(event.target)) {
        setShowRegisterDropdown(false);
      }
      if (partnersDropdownRef.current && !partnersDropdownRef.current.contains(event.target)) {
        setShowPartnersDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setCardsVisible(true);
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.2 }
    );
    if (whyTmgRef.current) observer.observe(whyTmgRef.current);
    return () => observer.disconnect();
  }, []);

  return (
    <div className="min-h-screen" style={{ backgroundColor: colors.bgCream }}>
      <Header />

      {/* Hero Section - Text directly on image, no box */}
      <section 
        className="relative min-h-screen overflow-hidden"
        data-testid="hero-section"
      >
        {/* Background Image */}
        <div 
          className="absolute inset-0 bg-cover bg-no-repeat"
          style={{
            backgroundImage: 'url(https://customer-assets.emergentagent.com/job_ecoconsu/artifacts/2hjxehu0_image.png)',
            backgroundPosition: 'center center',
          }}
        />
        
        {/* Subtle gradient overlay - lighter to show more image */}
        <div 
          className="absolute inset-0"
          style={{
            background: `linear-gradient(to right, 
              rgba(44, 24, 16, 0.7) 0%, 
              rgba(61, 107, 103, 0.5) 40%, 
              rgba(61, 107, 103, 0.3) 65%,
              rgba(61, 107, 103, 0.15) 100%
            )`
          }}
        />
        
        {/* Content - Vertically centered, positioned left */}
        <div className="relative z-10 min-h-screen flex items-center">
          <div className="w-full py-24 md:py-32">
            <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-16">
              
              {/* Hero Content - No box, text directly on image */}
              <div className="max-w-xl lg:max-w-2xl">
                
                {/* Small badge */}
                <div 
                  className="inline-flex items-center gap-2 px-4 py-2 rounded-full mb-8 opacity-0 animate-fade-in-up"
                  style={{ 
                    animationDelay: '0.1s', 
                    animationFillMode: 'forwards',
                    backgroundColor: 'rgba(77, 144, 120, 0.3)',
                    border: '1px solid rgba(255, 255, 255, 0.25)'
                  }}
                >
                  <span 
                    className="w-2 h-2 rounded-full animate-pulse"
                    style={{ backgroundColor: '#4D9078' }}
                  />
                  <span 
                    className="text-xs font-semibold tracking-widest uppercase"
                    style={{ color: '#FFFFFF' }}
                  >
                    India&apos;s Premier CSR Marketplace
                  </span>
                </div>
                
                {/* Main Heading - Strong text shadow for readability */}
                <h1 
                  className="text-4xl sm:text-5xl lg:text-6xl font-bold mb-6 leading-[1.05] tracking-tight opacity-0 animate-fade-in-up"
                  style={{ 
                    color: '#FFFFFF',
                    textShadow: '0 4px 30px rgba(0,0,0,0.5), 0 2px 10px rgba(0,0,0,0.3)',
                    animationDelay: '0.2s', 
                    animationFillMode: 'forwards'
                  }}
                  data-testid="hero-title"
                >
                  Connecting Purpose<br />with Partnership
                </h1>

                {/* Subheading */}
                <p 
                  className="text-lg md:text-xl mb-10 leading-relaxed max-w-lg opacity-0 animate-fade-in-up"
                  style={{ 
                    color: 'rgba(255, 255, 255, 0.95)',
                    textShadow: '0 2px 15px rgba(0,0,0,0.4)',
                    animationDelay: '0.3s', 
                    animationFillMode: 'forwards'
                  }}
                >
                  A curated platform bridging India&apos;s leading corporates with verified NGOs for credible, compliant, and measurable CSR impact.
                </p>

                {/* CTA Buttons */}
                <div 
                  className="flex flex-wrap gap-4 opacity-0 animate-fade-in-up"
                  style={{ animationDelay: '0.4s', animationFillMode: 'forwards' }}
                >
                  {/* Register Now - Primary CTA with teal color and pulse */}
                  <div className="relative" ref={registerDropdownRef}>
                    <button 
                      onClick={() => { setShowRegisterDropdown(!showRegisterDropdown); setShowPartnersDropdown(false); }}
                      className="inline-flex items-center gap-2.5 px-7 py-4 rounded-xl font-semibold transition-all duration-200 hover:-translate-y-0.5"
                      style={{ 
                        backgroundColor: showRegisterDropdown ? '#5A9A95' : '#4D8B86',
                        color: '#FFFFFF',
                        border: '2px solid rgba(255, 255, 255, 0.6)',
                        boxShadow: '0 4px 14px rgba(61, 107, 103, 0.4)',
                        animation: showRegisterDropdown ? 'none' : 'ctaPulse 2s ease-in-out infinite'
                      }}
                      data-testid="hero-register-btn"
                      onMouseEnter={(e) => {
                        e.currentTarget.style.backgroundColor = '#5A9A95';
                        e.currentTarget.style.borderColor = 'rgba(255, 255, 255, 0.9)';
                        e.currentTarget.style.animation = 'none';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.backgroundColor = showRegisterDropdown ? '#5A9A95' : '#4D8B86';
                        e.currentTarget.style.borderColor = 'rgba(255, 255, 255, 0.6)';
                        if (!showRegisterDropdown) e.currentTarget.style.animation = 'ctaPulse 2s ease-in-out infinite';
                      }}
                    >
                      Register Now
                      <ChevronDown className={`w-5 h-5 transition-transform duration-200 ${showRegisterDropdown ? 'rotate-180' : ''}`} />
                    </button>
                    
                    {showRegisterDropdown && (
                      <div 
                        className="absolute top-full left-0 mt-3 w-72 rounded-2xl overflow-hidden z-50"
                        style={{ backgroundColor: colors.bgCard, boxShadow: '0 25px 50px rgba(44, 24, 16, 0.3)', border: `1px solid ${colors.borderWarm}` }}
                      >
                        <button
                          onClick={() => { navigate('/xchange/corporate/register'); setShowRegisterDropdown(false); }}
                          className="w-full px-5 py-4 text-left transition-colors flex items-center gap-4"
                          style={{ backgroundColor: 'transparent' }}
                          onMouseEnter={(e) => e.currentTarget.style.backgroundColor = colors.bgSage}
                          onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                          data-testid="register-sponsor-btn"
                        >
                          <div className="w-11 h-11 rounded-xl flex items-center justify-center" style={{ backgroundColor: colors.bgSage }}>
                            <Building2 className="w-5 h-5" style={{ color: colors.primaryTeal }} />
                          </div>
                          <div>
                            <div className="font-semibold text-sm" style={{ color: colors.textPrimary }}>As Sponsor Company</div>
                            <div className="text-xs" style={{ color: colors.textTertiary }}>Corporate CSR teams</div>
                          </div>
                        </button>
                        <div style={{ borderTop: `1px solid ${colors.borderWarm}` }} />
                        <button
                          onClick={() => { navigate('/xchange/ngo/register'); setShowRegisterDropdown(false); }}
                          className="w-full px-5 py-4 text-left transition-colors flex items-center gap-4"
                          style={{ backgroundColor: 'transparent' }}
                          onMouseEnter={(e) => e.currentTarget.style.backgroundColor = colors.bgSage}
                          onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                          data-testid="register-ngo-btn"
                        >
                          <div className="w-11 h-11 rounded-xl flex items-center justify-center" style={{ backgroundColor: colors.bgSage }}>
                            <Users className="w-5 h-5" style={{ color: colors.primaryTeal }} />
                          </div>
                          <div>
                            <div className="font-semibold text-sm" style={{ color: colors.textPrimary }}>As NGO / Agency</div>
                            <div className="text-xs" style={{ color: colors.textTertiary }}>Implementing partners</div>
                          </div>
                        </button>
                      </div>
                    )}
                  </div>

                  {/* Verified Partners - Secondary CTA with same teal style */}
                  <div className="relative" ref={partnersDropdownRef}>
                    <button 
                      onClick={() => { setShowPartnersDropdown(!showPartnersDropdown); setShowRegisterDropdown(false); }}
                      className="inline-flex items-center gap-2.5 px-7 py-4 rounded-xl font-semibold transition-all duration-200 hover:-translate-y-0.5"
                      style={{ 
                        backgroundColor: showPartnersDropdown ? '#5A9A95' : '#4D8B86',
                        color: '#FFFFFF',
                        border: '2px solid rgba(255, 255, 255, 0.6)',
                        boxShadow: '0 4px 14px rgba(61, 107, 103, 0.4)'
                      }}
                      data-testid="hero-partners-btn"
                      onMouseEnter={(e) => {
                        e.currentTarget.style.backgroundColor = '#5A9A95';
                        e.currentTarget.style.borderColor = 'rgba(255, 255, 255, 0.9)';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.backgroundColor = showPartnersDropdown ? '#5A9A95' : '#4D8B86';
                        e.currentTarget.style.borderColor = 'rgba(255, 255, 255, 0.6)';
                      }}
                    >
                      <CheckCircle className="w-5 h-5" />
                      Verified Partners
                      <ChevronDown className={`w-5 h-5 transition-transform duration-200 ${showPartnersDropdown ? 'rotate-180' : ''}`} />
                    </button>
                    
                    {showPartnersDropdown && (
                      <div 
                        className="absolute top-full left-0 mt-3 w-72 rounded-2xl overflow-hidden z-50"
                        style={{ backgroundColor: colors.bgCard, boxShadow: '0 25px 50px rgba(44, 24, 16, 0.3)', border: `1px solid ${colors.borderWarm}` }}
                      >
                        <button
                          onClick={() => { navigate('/partners/sponsors'); setShowPartnersDropdown(false); }}
                          className="w-full px-5 py-4 text-left transition-colors flex items-center gap-4"
                          style={{ backgroundColor: 'transparent' }}
                          onMouseEnter={(e) => e.currentTarget.style.backgroundColor = colors.bgSage}
                          onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                        >
                          <div className="w-11 h-11 rounded-xl flex items-center justify-center" style={{ backgroundColor: colors.bgSage }}>
                            <Building2 className="w-5 h-5" style={{ color: colors.primaryBrown }} />
                          </div>
                          <div>
                            <div className="font-semibold text-sm" style={{ color: colors.textPrimary }}>Sponsor Companies</div>
                            <div className="text-xs" style={{ color: colors.textTertiary }}>View corporate partners</div>
                          </div>
                        </button>
                        <div style={{ borderTop: `1px solid ${colors.borderWarm}` }} />
                        <button
                          onClick={() => { navigate('/partners/ngos'); setShowPartnersDropdown(false); }}
                          className="w-full px-5 py-4 text-left transition-colors flex items-center gap-4"
                          style={{ backgroundColor: 'transparent' }}
                          onMouseEnter={(e) => e.currentTarget.style.backgroundColor = colors.bgSage}
                          onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                        >
                          <div className="w-11 h-11 rounded-xl flex items-center justify-center" style={{ backgroundColor: colors.bgSage }}>
                            <Users className="w-5 h-5" style={{ color: colors.primaryBrown }} />
                          </div>
                          <div>
                            <div className="font-semibold text-sm" style={{ color: colors.textPrimary }}>NGOs & Agencies</div>
                            <div className="text-xs" style={{ color: colors.textTertiary }}>View implementing partners</div>
                          </div>
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Trusted By Section - Soft Sage with Trust Badges */}
      <section className="py-6" style={{ backgroundColor: colors.bgSage, borderBottom: `1px solid ${colors.borderSage}` }}>
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="flex flex-wrap items-center justify-center gap-x-8 gap-y-4">
            {/* Trust Badges */}
            <div className="flex items-center gap-2">
              <CheckCircle className="w-4 h-4" style={{ color: colors.success }} />
              <span className="text-sm font-medium" style={{ color: colors.textSecondary }}>SOC 2 Compliant</span>
            </div>
            <div className="flex items-center gap-2">
              <CheckCircle className="w-4 h-4" style={{ color: colors.success }} />
              <span className="text-sm font-medium" style={{ color: colors.textSecondary }}>ISO 27001</span>
            </div>
            
            {/* Divider */}
            <div className="hidden sm:block w-px h-5" style={{ backgroundColor: colors.borderSage }} />
            
            {/* Trusted CSR Partner Tagline */}
            <span className="text-sm font-semibold" style={{ color: colors.textSecondary }}>
              Trusted CSR Enablement Partner for India&apos;s Leading Organizations
            </span>
          </div>
        </div>
      </section>

      {/* Why CSR Exchange Section - Warm Cream */}
      <section className="py-24 md:py-32" style={{ backgroundColor: colors.bgCream }}>
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-16">
          <div 
            ref={whyTmgRef}
            className={`text-center mb-16 transition-all duration-700 ${cardsVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
          >
            <p className="text-xs font-semibold tracking-widest uppercase mb-4" style={{ color: colors.textTertiary }}>
              Why CSR Exchange
            </p>
            <h2 className="text-2xl md:text-3xl lg:text-4xl font-bold tracking-tight" style={{ color: colors.textPrimary }}>
              Built for India&apos;s CSR Ecosystem
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {whyFeatures.map((item, idx) => (
              <div 
                key={idx}
                className={`rounded-2xl p-8 transition-all duration-500 cursor-pointer group ${cardsVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-12'}`}
                style={{ 
                  backgroundColor: colors.bgCard,
                  border: `1px solid ${colors.borderWarm}`,
                  transitionDelay: cardsVisible ? `${150 + idx * 100}ms` : '0ms'
                }}
                data-testid={`feature-card-${idx}`}
                onMouseEnter={(e) => {
                  e.currentTarget.style.borderColor = colors.primaryTeal;
                  e.currentTarget.style.boxShadow = `0 0 0 1px ${colors.primaryTeal}, 0 12px 24px rgba(61, 107, 103, 0.1)`;
                  e.currentTarget.style.transform = 'translateY(-4px)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.borderColor = colors.borderWarm;
                  e.currentTarget.style.boxShadow = 'none';
                  e.currentTarget.style.transform = 'translateY(0)';
                }}
              >
                <div 
                  className="w-14 h-14 rounded-xl flex items-center justify-center mb-6 transition-all duration-300 group-hover:scale-110 group-hover:rotate-3"
                  style={{ backgroundColor: colors.bgSage }}
                >
                  <item.icon className="w-6 h-6 transition-transform duration-300 group-hover:scale-110" style={{ color: colors.primaryTeal }} />
                </div>
                <h3 className="text-lg font-semibold mb-3" style={{ color: colors.textPrimary }}>
                  {item.title}
                </h3>
                <p className="text-sm leading-relaxed" style={{ color: colors.textSecondary }}>
                  {item.description}
                </p>
              </div>
            ))}
          </div>

          <div 
            className={`text-center mt-14 transition-all duration-500 ${cardsVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
            style={{ transitionDelay: cardsVisible ? '650ms' : '0ms' }}
          >
            <button 
              onClick={() => navigate('/about-us')}
              className="inline-flex items-center gap-2.5 px-7 py-4 rounded-xl font-semibold transition-all duration-200 hover:-translate-y-0.5"
              style={{ 
                backgroundColor: colors.primaryTeal,
                color: colors.textOnDark,
                boxShadow: '0 4px 14px rgba(61, 107, 103, 0.3)'
              }}
              data-testid="learn-more-btn"
              onMouseEnter={(e) => e.currentTarget.style.backgroundColor = colors.primaryTealLight}
              onMouseLeave={(e) => e.currentTarget.style.backgroundColor = colors.primaryTeal}
            >
              Learn More About Us
              <ArrowRight className="w-5 h-5" />
            </button>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default HomePage;
